import type { Query, QueryClient, QueryKey } from "@tanstack/react-query";

/**
 * Coalesces live invalidations while a cached query is already fetching.
 *
 * A plain `invalidateQueries` cancels an in-flight refetch and starts another,
 * but the cancelled request may still run to completion on the server. Under a
 * steady stream of writes that stacked one copy of each read per write. Here an
 * idle query refetches now; a query that is already fetching keeps its request
 * and refetches once more after it settles, so the latest change still lands.
 * `meta.liveRefreshIntervalMs` adds a cooldown after a read settles.
 */
export function createLiveRefresh(queryClient: QueryClient): {
	refresh: (queryKey: QueryKey) => void;
	dispose: () => void;
} {
	const cache = queryClient.getQueryCache();
	// Fetching when a change arrived: owes one refetch once it settles.
	const owed = new Set<Query>();
	// Re-read recently: refetches when its interval has passed.
	const waiting = new Map<Query, ReturnType<typeof setTimeout>>();

	function refreshQuery(query: Query) {
		if (query.state.fetchStatus !== "idle") {
			owed.add(query);
			return;
		}
		if (waiting.has(query)) return;
		const interval = query.meta?.liveRefreshIntervalMs;
		const settledAt = Math.max(
			query.state.dataUpdatedAt,
			query.state.errorUpdatedAt,
		);
		const wait =
			typeof interval === "number" && query.isActive()
				? settledAt + interval - Date.now()
				: 0;
		if (wait > 0) {
			waiting.set(
				query,
				setTimeout(() => {
					waiting.delete(query);
					refreshQuery(query);
				}, wait),
			);
			return;
		}
		void queryClient.invalidateQueries(
			{ queryKey: query.queryKey, exact: true },
			{ cancelRefetch: false },
		);
	}

	const unsubscribe = cache.subscribe((event) => {
		const { query } = event;
		if (event.type === "removed") {
			owed.delete(query);
			clearTimeout(waiting.get(query));
			waiting.delete(query);
		} else if (
			event.type === "updated" &&
			query.state.fetchStatus === "idle" &&
			owed.delete(query)
		) {
			refreshQuery(query);
		}
	});

	return {
		refresh(queryKey) {
			for (const query of cache.findAll({ queryKey })) refreshQuery(query);
		},
		dispose() {
			unsubscribe();
			// Keep deferred changes stale for the next mount without refetching.
			for (const [query, timer] of waiting) {
				clearTimeout(timer);
				query.invalidate();
			}
			waiting.clear();
			for (const query of owed) {
				// A successful in-flight read clears invalidation when it lands.
				void query.promise?.then(
					() => query.invalidate(),
					() => query.invalidate(),
				);
			}
			owed.clear();
		},
	};
}
